/**
 * Сверяет сборку открытой вкладки с той, что сейчас на сервере.
 * Inertia ловит деплой только на переходах (409), а шот-панель, флоу и прочие экраны
 * живут на fetch: вкладка со старым JS получает данные нового бэкенда и может упасть.
 */

const CHECK_URL = "/app_version"
const CHECK_INTERVAL_MS = 10 * 60 * 1000
const FOCUS_CHECK_THROTTLE_MS = 60 * 1000
const AUTO_RELOAD_KEY = "it2tv:auto-reload-version"

let loadedVersion: string | null | undefined
let serverVersion: string | null = null
let lastCheckAt = 0
let pendingCheck: Promise<boolean> | null = null
const listeners = new Set<() => void>()

/** Build this tab runs. Null when the server does not version assets (development). */
function readLoadedVersion(): string | null {
  if (loadedVersion === undefined) {
    loadedVersion = document.querySelector<HTMLMetaElement>('meta[name="app-version"]')?.content || null
  }
  return loadedVersion
}

export function isAppOutdated(): boolean {
  const loaded = readLoadedVersion()
  return loaded != null && serverVersion != null && serverVersion !== loaded
}

export function subscribeAppOutdated(listener: () => void): () => void {
  listeners.add(listener)
  return () => {
    listeners.delete(listener)
  }
}

/** Asks the server for its build. Resolves true when this tab runs an older one. */
function checkAppVersion(): Promise<boolean> {
  if (readLoadedVersion() == null) return Promise.resolve(false)
  pendingCheck ??= fetch(CHECK_URL, { cache: "no-store", headers: { Accept: "application/json" } })
    .then((response) => (response.ok ? (response.json() as Promise<{ version?: unknown }>) : null))
    .then((body) => {
      if (typeof body?.version === "string" && body.version !== serverVersion) {
        serverVersion = body.version
        listeners.forEach((listener) => listener())
      }
      return isAppOutdated()
    })
    .catch(() => isAppOutdated())
    .finally(() => {
      lastCheckAt = Date.now()
      pendingCheck = null
    })
  return pendingCheck
}

/**
 * After a failure: if the server already runs a newer build, reload once per that build.
 * A build that keeps failing then shows the reload prompt instead of looping.
 */
export async function reloadIfOutdated(): Promise<void> {
  if (!(await checkAppVersion()) || serverVersion == null) return
  try {
    if (sessionStorage.getItem(AUTO_RELOAD_KEY) === serverVersion) return
    sessionStorage.setItem(AUTO_RELOAD_KEY, serverVersion)
  } catch {
    // Без storage не отличить второй заход от первого — не рискуем циклом перезагрузок.
    return
  }
  window.location.reload()
}

/** Checks on return to the tab and periodically while it stays visible. */
export function startAppVersionWatch(): void {
  if (readLoadedVersion() == null) return
  const checkWhenVisible = () => {
    if (document.visibilityState !== "visible") return
    if (Date.now() - lastCheckAt < FOCUS_CHECK_THROTTLE_MS) return
    void checkAppVersion()
  }
  document.addEventListener("visibilitychange", checkWhenVisible)
  window.addEventListener("focus", checkWhenVisible)
  window.setInterval(checkWhenVisible, CHECK_INTERVAL_MS)
  // Чанк старой сборки после деплоя уже удалён с сервера.
  window.addEventListener("vite:preloadError", () => {
    void reloadIfOutdated()
  })
}
